import { objectRequest } from '../shared/object-request';
import { ftCurrentTruthSink } from '../shared/truth-sink';
import type { CombatProgress } from '../shared/combat-settlement';
// combat-start-truth.ts — R-117 slice L3b + R-128 (Janus lane N5b, 2026-10-01): the page-side half of a browser-decided combat START.
//
// The combat-truth lane (combat-truth.ts commit('start')) asks this module for the start plan before it posts. Here: the island (the SAME
// resolver the GM's pick panel shows — roll-init/client-combat-membership.ts over the FT3D scene's preview truth), the participants'
// full compiles and the definitions the engine reads, the readers, then janus-engine/combat/start-roll.ts planCombatStart() on the
// browser's dice. Nothing here decides a rule; nothing falls back to PHP — a gate refuses the start in the page.
// R-131 J1 (lane N5d, 2026-10-01): the GM's mid-combat JOIN rides the same module (planBrowserJoin — the same island resolver, then
// janus-engine/combat/join-plan.ts).
import { isSelfStatusOrigin } from '../janus-engine/statuses/status-origin.js';
import { planCombatStart, planCombatStartYielding, planCombatEndContingencies, contingencyParse, INITIATIVE_SKILL_ID, FORTUNATE_ABILITY_ID, COMBAT_START_ENGINE, type CombatStartPlan, type CombatStartReaders, type CombatStartAppliers, type StartParticipant } from '../janus-engine/combat/start-roll.js';
import { planCombatJoin, joinIslandInputs, combatMemberIds, JOIN_MAX, type CombatJoinPlan, type JoinMode } from '../janus-engine/combat/join-plan.js';
import type { RefillOp } from '../janus-engine/charges/combat-refill.js';
import type { CampaignClock } from '../janus-engine/ability/context.js';
import type { CombatStartOpening } from '../janus-engine/combat/start-plan.js';
import { inventoryFloorRefusal } from '../janus-engine/compile/inventory-facet.js';
import { compileFetchedEntity } from '../shared/compile-fetched-entity';
import type { ExitReaders } from '../janus-engine/combat/exit-plan.js';
import { browserRollSource } from '../janus-engine/actions/roll-source.js';
import { removeStatusFromRow, statusRows, statusItemId } from '../janus-engine/statuses/expiry.js';
import { CONTINGENCY_ARMING_IDS } from '../janus-engine/sanguine/sanguine-gate.js';
import { resolveClientCombatIsland, type ClientCombatNoise, type ClientCombatTruth } from '../roll-init/client-combat-membership.js';
import { ftCampaignTimeAddSeconds, ftClockCalendarFromArr, ftClockColsFromArr } from '../shared/campaign-clock.js';
import { abilityTruthReaders } from './ability-truth.js';
import { fetchAndCacheCaster, withSettledCasterReads } from './caster-cache.js';
import { fetchChargeRows } from './c1-possessor-shadow.js';
import { ABILITY_CHARGE_BUCKETS, abilityEntryId } from '../janus-engine/ability/charges/charge-entry.js';
import { entryCharges } from '../janus-engine/actions/ability-preconditions.js';
import { setRowAbilityCharges, upsertStatusOnRow } from './time-truth.js';
import { turnReaders } from './turn-truth.js';
import { MAX_ASK_ROUNDS, askTriggerConsent, createTriggerConsent } from './trigger-consent.js';
import { fullDefinition } from '../janus-engine/reactions/php-row-rules.js';
import { definitionOf } from './charge-truth.js';

type Row = Record<string, unknown>;
const asRow = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const scalar = (v: unknown): unknown => (Array.isArray(v) ? v[0] : v);
const upper = (v: unknown): string => String(scalar(v) ?? '').trim().toUpperCase();
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const ecOf = (row: Row): Row => asRow(row.encounter_creatures) ?? {};
const idOf = (row: Row): string => upper(ecOf(row).encounter_creature_id);
const encounterFor = (id: string): Row | null => asRow(window.FateTide?.encounterCache?.get?.(upper(id)) ?? window.FateTide?.encounterCache?.get?.(id));
const rosterOf = (encounter: Row | null): Row[] => (Array.isArray(encounter?.encounter_creatures_arr) ? encounter!.encounter_creatures_arr as unknown[] : []).map(asRow).filter((r): r is Row => !!r && !!idOf(r));
const sleep = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

type IslandGate = { kind: 'gate'; reason: string };
function unportedIslandGate(error: unknown): IslandGate {
  if (error instanceof Error && error.message.startsWith('unported:')) return { kind: 'gate', reason: error.message.slice(9) };
  throw error;
}

/** The FT3D scene's query-only preview truth (the same object the pick panel reads): the compiled shape + the GM light grid. */
function previewTruthNow(encounterId: string): ClientCombatTruth | null {
  const frame = document.querySelector<HTMLIFrameElement>('[data-ft3d-encounter-frame]');
  let raw: Row | null = null;
  try { raw = asRow(((frame?.contentWindow ?? window) as unknown as { ft3dGetCombatPreviewTruth?: () => unknown }).ft3dGetCombatPreviewTruth?.()); } catch { raw = null; }
  if (!raw || raw.schema !== 'ft.ft3d.combat-preview-truth.v1' || upper(raw.encounterId) !== upper(encounterId)) return null;
  const shape = asRow(raw.shape);
  if (!shape || !Array.isArray(shape.boxes)) return null;
  return { shape: shape as unknown as ClientCombatTruth['shape'], lightGrid: raw.lightGrid as ClientCombatTruth['lightGrid'] };
}
async function previewTruth(encounterId: string, waitMs = 20000): Promise<ClientCombatTruth | null> {
  for (const started = Date.now(); ; await sleep(250)) {
    const truth = previewTruthNow(encounterId);
    if (truth || Date.now() - started > waitMs) return truth;
  }
}

/** What the start planner's live readers read, watched across its pauses (fix-bug 2026-10-13). Cheap checks after EVERY pause: a write
 *  through the page's object / caster / campaign / encounter cache Maps, a cache Map or one of its read methods replaced, a reader global
 *  replaced, or an in-place inventory repaint (the base runtime's announced recompile — websocket/base-patch.ts). Before the REAL pass,
 *  also the held encounter's own serialization (every roster row the planner reads lives in it; the page keeps the inventory envelope off
 *  it, so this stays ~1–2 MB). A whole-cache serialization per pause was measured at ~2 s each on a 12-combatant start — too slow to
 *  watch with. Not watched: the campaign clock's value — the synchronous planner read it at moments apart too. */
const WATCHED_CACHES = ['objectCache', 'casterCache', 'campaignCache', 'encounterCache'] as const;
/** …and the page globals the readers call (ability-truth.ts readers, the layer readers): a swapped resolver is a changed input too. */
const WATCHED_GLOBALS = ['ftResolveCachedInventoryRow', 'ftResolveGroundExitPlacement', 'ftLayerValue', 'ftLayerEntries', 'ftInventoryField', 'ftItemsCol',
  'ftCreatureStatuses', 'ftCampaignClock'] as const;
const WATCHED_EVENTS = ['ft:inventory-base-compiled', 'ft:inventory-base-refused'] as const;
function watchPlanInputs(encounterId: string): { unchanged: (beforeReal: boolean) => boolean; stop: () => void } {
  const ft = (): Record<string, unknown> | undefined => window.FateTide as unknown as Record<string, unknown> | undefined;
  const page = window as unknown as Record<string, unknown>;
  const maps = WATCHED_CACHES.map(name => ft()?.[name]);
  const globals = WATCHED_GLOBALS.map(name => page[name]);
  // the caches' READ methods as the readers will call them (a patched get/has is a changed input)
  const readMethods = (): unknown[] => maps.flatMap(cache => cache instanceof Map ? (['get', 'has', 'entries', 'values', 'keys', 'forEach'] as const).map(m => (cache as unknown as Record<string, unknown>)[m]) : []);
  const reads = readMethods();
  let written = false;
  const restore: Array<() => void> = [];
  for (const cache of maps) {
    if (!(cache instanceof Map)) continue;
    const target = cache as unknown as Record<string, unknown>;
    for (const method of ['set', 'delete', 'clear'] as const) {
      const own = Object.prototype.hasOwnProperty.call(cache, method) ? target[method] : undefined;
      const original = target[method] as (...args: unknown[]) => unknown;
      target[method] = function (this: unknown, ...args: unknown[]) { written = true; return original.apply(this, args); };
      restore.push(() => { if (own !== undefined) target[method] = own; else delete target[method]; });
    }
  }
  const repainted = (): void => { written = true; };
  // (a host without DOM events — a test VM — has no repaint to announce)
  if (typeof window.addEventListener === 'function')
    for (const name of WATCHED_EVENTS) { window.addEventListener(name, repainted); restore.push(() => window.removeEventListener?.(name, repainted)); }
  const fingerprint = (): string | null => { try { return JSON.stringify(encounterFor(encounterId)); } catch { return null; } };
  const before = fingerprint();
  // the island's floor gate (the inventory bridge's verdict, a deploy's commit pause included) — a floor refused mid-plan is a moved input
  const floor = (): string => { try { const e = encounterFor(encounterId); return e ? inventoryFloorRefusal(e) ?? 'ok' : 'no-encounter'; } catch { return 'threw'; } };
  const floorBefore = floor();
  const cheap = (): boolean => !written && WATCHED_CACHES.every((name, i) => ft()?.[name] === maps[i]) && WATCHED_GLOBALS.every((name, i) => page[name] === globals[i])
    && readMethods().every((method, i) => method === reads[i]) && floorBefore !== 'threw' && floor() === floorBefore;
  return {
    unchanged: beforeReal => cheap() && (!beforeReal || (before !== null && fingerprint() === before)),
    stop: () => { for (const undo of restore.reverse()) undo(); },
  };
}

/** The island (ft_resolve_initial_combat_participant_ids' browser twin): no seed → every eligible player character. */
export function startIsland(encounter: Row, truth: ClientCombatTruth, seedIds: string[], excludedIds: string[]): StartParticipant[] {
  const excluded = new Set(excludedIds.map(upper));
  const eligiblePc = (row: Row): boolean => String(scalar(ecOf(row).item_type) ?? scalar(asRow(row.items)?.item_type) ?? '').toLowerCase() === 'player_character'
    && Number(scalar(ecOf(row).encounter_creature_dead) ?? 0) !== 1 && Number(scalar(ecOf(row).encounter_creature_private) ?? 0) !== 1 && !excluded.has(idOf(row));
  const seeds = seedIds.length ? seedIds.map(upper) : rosterOf(encounter).filter(eligiblePc).map(idOf);
  const island = resolveClientCombatIsland(encounter, truth, seeds, [...excluded]);
  return island.participantIds.map(id => ({ id: upper(id), reason: island.reasons[id] ?? 'sight' }));
}

/** Reuse the full catalog already compiled with the roster, including interned children. */
function heldDefinition(id: string): boolean {
  let held = fullDefinition(definitionOf(id), 'any');
  const finder = (window as unknown as { ftFindFullNestedItem?: (id: string) => unknown }).ftFindFullNestedItem;
  if (!held) try { held = fullDefinition(asRow(finder?.(id)), 'any'); } catch { /* Not mounted on every page. */ }
  if (!held) return false;
  (window.FateTide as unknown as { objectCache?: Map<string, unknown> } | undefined)?.objectCache?.set(id, held);
  return true;
}

/** Initiative reads its saved name/type/roll_type; its tier and grants are already on each compiled member.
 * The display catalog may omit this skill, but both room transports retain its SELECT row. No card compile is owed. */
function heldInitiativeDefinition(encounterId: string): Row | null {
  const base = ftCurrentTruthSink()?.heldBase?.(encounterId)
    ?? window.ftReadHeldEntityBase?.(encounterId, 'encounter')?.base;
  return fullDefinition(asRow(asRow(base?.catalog)?.[INITIATIVE_SKILL_ID]), 'any');
}

/** Read only missing definitions (≤ 64 ids per POST); adoption retains four concurrent siblings. */
async function fetchObjects(ids: readonly string[], progress?: CombatProgress): Promise<void> {
  const wanted = [...new Set(ids.map(upper).filter(id => UUID.test(id) && !heldDefinition(id)))];
  let completed = 0;
  const ft = (window.FateTide ??= {} as never) as unknown as { objectCache?: Map<string, unknown> };
  ft.objectCache ??= new Map();
  await Promise.all(Array.from({ length: Math.ceil(wanted.length / 64) }, async (_, i) => {
    try {
      const body = await objectRequest(async signal => {
        const response = await fetch('/options/api/object_inc.php', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids: wanted.slice(i * 64, i * 64 + 64) }), signal });
        return response.ok ? asRow(await response.json()) : null;
      });
      // Four siblings share a catalog request instead of serial closure round trips.
      const objects = Object.entries(asRow(body?.objects) ?? {});
      for (let offset = 0; offset < objects.length; offset += 4) {
        await Promise.all(objects.slice(offset, offset + 4).map(async ([id, object]) => {
          const o = asRow(object);
          if (!o || o.missing === true || o.forbidden === true) return;
          try {
            const adopted = await compileFetchedEntity(o);
            if (asRow(adopted.items)) ft.objectCache!.set(upper(id), adopted);
          } catch { /* Required gates or optional skips report the unheld definition. */ }
          progress?.(`Loading combat abilities (${++completed}/${wanted.length})…`);
        }));
      }
    } catch { /* Missing ids remain unheld for required gates or optional skips. */ }
  }));
}

/** The campaign clock the page holds (combat freezes it) and its calendar arithmetic. */
function clockReaders(campaignId: string): Pick<CombatStartReaders, 'clockNow' | 'addSeconds'> {
  const campaign = asRow(window.FateTide?.campaignCache?.get?.(upper(campaignId)) ?? window.FateTide?.campaignCache?.get?.(campaignId));
  const calendar = campaign ? ftClockCalendarFromArr(campaign) : null;
  return {
    clockNow: (): CampaignClock | null => {
      const derived = asRow(asRow((window as unknown as { ftCampaignClock?: { getDerived?: () => unknown } }).ftCampaignClock?.getDerived?.())?.cols);
      if (derived && Number.isFinite(Number(derived.year))) return { year: Number(derived.year), month: Number(derived.month), day: Number(derived.day), hour: Number(derived.hour), minute: Number(derived.minute), second: Number(derived.second) };
      return campaign ? ftClockColsFromArr(campaign) : null;
    },
    addSeconds: (clock, seconds) => { if (!calendar?.months.length) throw new Error('Campaign calendar is unavailable'); return ftCampaignTimeAddSeconds(clock, calendar, seconds); },
  };
}
const APPLIERS: CombatStartAppliers = { upsertStatus: upsertStatusOnRow, removeStatus: removeStatusFromRow, setCharges: (row, itemId, current) => { setRowAbilityCharges(row, itemId, current); } };
/** Full compiles of the given rows (the engine reads computed skills / ability buckets), 4 at a time; false = a fetch failed. */
async function hydrateCompiles(ids: readonly string[], progress?: CombatProgress): Promise<boolean> {
  let completed = 0;
  progress?.(`Loading combatants (0/${ids.length})…`);
  for (let i = 0; i < ids.length; i += 4) {
    const results = await Promise.allSettled(ids.slice(i, i + 4).map(async id => {
      // Readiness already joined required revisions. Reuse the full canonical row;
      // only a genuinely cold/lean member owes a saved-base read and compile.
      await withSettledCasterReads(() => fetchAndCacheCaster(id, { throwOnError: true }));
      progress?.(`Loading combatants (${++completed}/${ids.length})…`);
    }));
    if (results.some(r => r.status === 'rejected')) return false;
  }
  return true;
}

export type BrowserEndContingencyOutcome = { kind: 'plan'; contingencies: CombatStartPlan['contingencies']; warnings: string[] } | { kind: 'gate'; reason: string; message?: string };
/** R-128 (lane N5b): the EXIT's combat_ends contingencies, fired by the page (start-roll.ts planCombatEndContingencies over A3's engine-fired
 *  cast). Nobody holding one → an empty set without a request. */
export async function planBrowserEndContingencies(input: { campaignId: string; encounterId: string; rollPrivate: number; exitReaders: ExitReaders }): Promise<BrowserEndContingencyOutcome> {
  const encounterId = upper(input.encounterId);
  let encounter = encounterFor(encounterId);
  if (!encounter) return { kind: 'gate', reason: 'encounter-arr' };
  const holds = (r: Row): boolean => statusRows(r, 'target').some(st => CONTINGENCY_ARMING_IDS.includes(statusItemId(st)) && contingencyParse(st.status_casting_targets_arr).cond === 'combat_ends');
  const owners = rosterOf(encounter).filter(r => Number(scalar(ecOf(r).encounter_creature_deleted) ?? 0) === 0 && holds(r)).map(idOf);
  if (!owners.length) return { kind: 'plan', contingencies: { steps: [], skipped: [] }, warnings: [] };
  if (!await hydrateCompiles(owners)) return { kind: 'gate', reason: 'compile-fetch-failed' };
  encounter = encounterFor(encounterId);
  if (!encounter) return { kind: 'gate', reason: 'encounter-arr' };
  const defIds = new Set<string>();
  for (const row of rosterOf(encounter)) if (owners.includes(idOf(row))) for (const st of statusRows(row, 'target')) { defIds.add(statusItemId(st)); if (!isSelfStatusOrigin(upper(st.status_id), upper(st.status_origin_id)) && UUID.test(upper(st.status_origin_id))) defIds.add(upper(st.status_origin_id)); }
  const objectCache = (window.FateTide as unknown as { objectCache?: Map<string, unknown> } | undefined)?.objectCache;
  await fetchObjects([...defIds].filter(id => !asRow(asRow(objectCache?.get(id))?.items)));
  await hydrateReferences([...defIds]);
  const tried = new Set<string>();
  for (let pass = 0; pass < 5; pass++) {
    encounter = encounterFor(encounterId);
    if (!encounter) return { kind: 'gate', reason: 'encounter-arr' };
    const first = rosterOf(encounter).find(r => idOf(r) === owners[0]) ?? rosterOf(encounter)[0]!;
    const readers: CombatStartReaders = { ability: abilityTruthReaders(first, encounter, new Set(owners)), turn: work => turnReaders(work), exit: input.exitReaders,
      ...clockReaders(input.campaignId), uuid: () => crypto.randomUUID().toUpperCase() };
    const outcome = planCombatEndContingencies({ encounterId, rollPrivate: input.rollPrivate }, encounter, readers, APPLIERS, browserRollSource());
    if (outcome.kind === 'plan') return outcome;
    if (outcome.kind === 'refuse') return { kind: 'gate', reason: outcome.reason, message: outcome.message };
    const fresh = outcome.ids.filter(id => !tried.has(id));
    if (!fresh.length) return { kind: 'gate', reason: 'contingency-definitions' };
    fresh.forEach(id => tried.add(id));
    await hydrateReferences(fresh);
  }
  return { kind: 'gate', reason: 'contingency-definitions' };
}

/** The definitions an engine-fired cast reads beyond its own (the keys ability-truth.ts hydrates before a use — the statuses it applies /
 *  removes, its contest skills, its damage types; a concentration spell's Concentrating), two levels deep, batched. An unheld one would
 *  hand the fire back pre-draw with no id to fetch. */
const REFERENCE_KEYS = ['status_effect_applied_to_caster_after_cast', 'status_effect_applied_to_target_after_cast', 'status_effect_applied_to_caster_on_hit',
  'status_effect_applied_to_target_on_hit', 'status_effect_applied_to_target_after_contesting_roll_failed', 'status_effect_removed_from_target_after_cast',
  'status_effect_removed_from_caster_after_cast', 'skill_roll_contester', 'skill_roll_contestee', 'damage_type'];
const CONCENTRATING = 'E7FDB796-26DA-E0F5-8F88-C21FE01D923B';
async function hydrateReferences(rootIds: readonly string[], progress?: CombatProgress): Promise<void> {
  const cache = (): Map<string, unknown> | undefined => (window.FateTide as unknown as { objectCache?: Map<string, unknown> } | undefined)?.objectCache;
  // Traversal is separate from fetching (Codex review A2 #2): every reference within the depth is walked whether or not it was already held,
  // and only the unheld ones are fetched — a warm middle link never hides a cold leaf.
  const seen = new Set<string>();
  let frontier = [...new Set(rootIds.map(upper))];
  for (let depth = 0; depth < 3 && frontier.length; depth++) {
    frontier.forEach(id => seen.add(id));
    const missing = frontier.filter(id => !heldDefinition(id));
    if (missing.length) await fetchObjects(missing, progress); // fetchObjects batches 64 per request — nothing dropped (Codex review C)
    const next = new Set<string>();
    for (const id of frontier) {
      const def = asRow(cache()?.get(id));
      if (!def) continue;
      for (const key of REFERENCE_KEYS) for (const entry of (window.ftLayerEntries?.(def, key) ?? []) as unknown[]) {
        const ref = upper(typeof entry === 'string' ? entry.split('|')[0] : asRow(asRow(entry)?.items)?.item_id ?? asRow(entry)?.item_id);
        if (UUID.test(ref) && !seen.has(ref)) next.add(ref);
      }
      if (window.ftLayerValue?.(def, 'spell_concentration') != null && !seen.has(CONCENTRATING)) next.add(CONCENTRATING);
    }
    frontier = [...next];
  }
  const leaves = frontier.filter(id => !heldDefinition(id));
  if (leaves.length) await fetchObjects(leaves, progress);
}
/** Every ability entry id a row carries (its combat-begins carriers live among them). */
function carrierIds(row: Row): string[] {
  const out = new Set<string>();
  for (const key of ['passive_ability', 'ability', 'full_ability', 'quick_ability', 'free_ability', 'reaction_ability', 'attack_ability', 'lair_ability']) {
    for (const entry of (window.ftLayerEntries?.(row, key) ?? []) as unknown[]) {
      const id = upper(typeof entry === 'string' ? entry : asRow(entry)?.item_id ?? asRow(asRow(asRow(entry)?.item_arr)?.items)?.item_id);
      if (UUID.test(id)) out.add(id);
    }
  }
  return [...out];
}

export type BrowserJoinOutcome = { kind: 'plan'; plan: CombatJoinPlan } | { kind: 'gate'; reason: string; message?: string };
/** R-131 J1 (lane N5d): the GM's mid-combat JOIN — the island from the SAME resolver the start uses (existing members ∪ the GM's picks, the
 *  removals minus the existing members, minus the existing members — the pick panel's own preview), then janus-engine/combat/join-plan.ts over
 *  the joiners' full compiles (their start-of-turn reset reads statuses + passives). Nothing falls back to PHP: a gate refuses in the page. */
export async function planBrowserJoin(input: { campaignId: string; encounterId: string; combatId: string; seedIds: string[]; excludedIds: string[]; exitReaders: ExitReaders;
  /** The scene truth when there is no FT3D frame to read it from (the request-mode test twin, tools/test/_combat_plan_node.mjs). */
  truth?: ClientCombatTruth;
  /** R-131 J2: a perception sweep (no GM picks — the island grows from the members alone; ft_combat_enrol_sweep's rule). */
  sweep?: boolean;
  /** R-131 J3 (lane N5e): the sound events of committed hostile actions — a sweep that also pulls in whoever HEARS them (mode 'noise'). */
  noise?: ClientCombatNoise[]; progress?: CombatProgress }): Promise<BrowserJoinOutcome> {
  const encounterId = upper(input.encounterId), combatId = upper(input.combatId);
  const noise = input.noise ?? [];
  const sweep = input.sweep === true || noise.length > 0;
  const mode: JoinMode = noise.length ? 'noise' : sweep ? 'sweep' : 'gm';
  if (!encounterFor(encounterId)) return { kind: 'gate', reason: 'encounter-arr' };
  if (!joinIslandInputs(encounterFor(encounterId)!, combatId, input.seedIds, input.excludedIds, { sweep })) return { kind: 'gate', reason: sweep || input.seedIds.length ? 'combat-changed' : 'no-seeds' };
  input.progress?.('Reading combat perception…');
  const truth = input.truth ?? await previewTruth(encounterId);
  if (!truth) return { kind: 'gate', reason: 'island-truth' };
  const picked = new Set(input.seedIds.map(upper));
  const sameSet = (a: string[], b: string[]): boolean => a.length === b.length && a.every(x => b.includes(x));
  const hydrated = new Set<string>();
  const tried = new Set<string>();
  // The island and the members it was grown from are ONE snapshot: the plan's members_before (the save's CAS) must be the membership the
  // island was computed from — a member another page enrolled while this one waited on compiles re-runs the island (coordinator Codex review
  // 2026-10-01 §B #2: a fresh members_before beside a stale island would pass the CAS yet never consider the new member's perception).
  for (let round = 0; round < 3; round++) {
    let encounter = encounterFor(encounterId);
    if (!encounter) return { kind: 'gate', reason: 'encounter-arr' };
    const inputs = joinIslandInputs(encounter, combatId, input.seedIds, input.excludedIds, { sweep });
    if (!inputs) return { kind: 'gate', reason: 'combat-changed' };
    const held = new Set(inputs.existing);
    // A GM pick is 'manual' (the panel's label); everything else carries the resolver's reason (sight / hearing / summon / lair).
    const islandOf = (e: Row): StartParticipant[] | IslandGate => {
      let island: ReturnType<typeof resolveClientCombatIsland>;
      try { island = resolveClientCombatIsland(e, truth, inputs.seeds, inputs.excluded, noise); }
      catch (error) { return unportedIslandGate(error); }
      return island.participantIds.map(upper).filter(id => !held.has(id)).map(id => ({ id, reason: picked.has(id) ? 'manual' : island.reasons[id] ?? 'sight' }));
    };
    const participants = islandOf(encounter);
    if (!Array.isArray(participants)) return participants;
    if (!participants.length) return { kind: 'gate', reason: 'no-joiners', message: 'Nobody would join this combat.' };
    if (participants.length > JOIN_MAX) return { kind: 'gate', reason: 'join-too-large' };
    const ids = participants.map(p => p.id);
    const cold = ids.filter(id => !hydrated.has(id));
    if (cold.length) {
      if (!await hydrateCompiles(cold, input.progress)) return { kind: 'gate', reason: 'compile-fetch-failed' };
      cold.forEach(id => hydrated.add(id));
      encounter = encounterFor(encounterId);
      if (!encounter) return { kind: 'gate', reason: 'encounter-arr' };
      // The definitions the start-of-turn reset reads: each joiner's statuses (+ their origins) and its carried abilities (the passives).
      const defIds = new Set<string>();
      for (const row of rosterOf(encounter)) {
        if (!cold.includes(idOf(row))) continue;
        for (const s of statusRows(row, 'target')) { defIds.add(statusItemId(s)); if (!isSelfStatusOrigin(upper(s.status_id), upper(s.status_origin_id)) && UUID.test(upper(s.status_origin_id))) defIds.add(upper(s.status_origin_id)); }
        for (const id of carrierIds(row)) defIds.add(id);
      }
      await fetchObjects([...defIds], input.progress);
    }
    let moved = false;
    for (let pass = 0; pass < 5; pass++) {
      encounter = encounterFor(encounterId);
      if (!encounter) return { kind: 'gate', reason: 'encounter-arr' };
      if (!sameSet(combatMemberIds(encounter, combatId), inputs.existing)) { moved = true; break; } // the membership moved: re-run the island
      // ...and so did the perception it was grown from (coordinator Codex round 4 #6): a creature that walked into / out of range while the
      // compiles loaded re-runs the island on the rows the plan is made from — the members CAS alone cannot see a position change.
      const now = islandOf(encounter);
      if (!Array.isArray(now)) return now;
      if (!sameSet(now.map(p => p.id), participants.map(p => p.id))) { moved = true; break; }
      // the same joiners — with the reasons of THIS read (lane N5f — coordinator Codex round 8 P3: a joiner that now qualifies by hearing instead of
      // sight used to be logged with the stale reason)
      input.progress?.(`Planning reinforcements (${pass + 1})…`);
      const outcome = planCombatJoin({ combatId, participants: now, mode }, encounter, { turn: work => turnReaders(work), exit: input.exitReaders }, COMBAT_START_ENGINE);
      if (outcome.kind === 'plan') return outcome;
      if (outcome.kind === 'refuse') return { kind: 'gate', reason: outcome.reason, message: outcome.message };
      const fresh = outcome.ids.filter(id => !tried.has(id));
      if (!fresh.length) return { kind: 'gate', reason: 'join-definitions' };
      fresh.forEach(id => tried.add(id));
      await fetchObjects(fresh, input.progress);
    }
    if (!moved) return { kind: 'gate', reason: 'join-definitions' };
  }
  // Three rounds and the membership / perception still moved under the plan: "not yet", never "that fight is gone" (Codex N5e TS-B #2 — a noise
  // sweep's sound used to be dropped here as `combat-changed`).
  return { kind: 'gate', reason: 'island-moving' };
}

/** `stale`: the page's state moved while the plan was yielding (watchPlanInputs) — nothing was drawn or sent; the caller decides the start
 *  again FROM THE TOP (gates, opening, island, hydration, plan), never re-plans this attempt's derived inputs (Astra review 2026-10-13). */
export type BrowserStartOutcome ={ kind: 'plan'; plan: CombatStartPlan; warnings: string[] } | { kind: 'gate'; reason: string; message?: string } | { kind: 'stale' };

export async function planBrowserStart(input: { campaignId: string; encounterId: string; seedIds: string[]; excludedIds: string[]; rollPrivate: number;
  opening: CombatStartOpening; charges: RefillOp[]; exitReaders: ExitReaders; preLastWins: RefillOp[];
  /** The scene truth when there is no FT3D frame to read it from (the request-mode test twin, tools/test/_combat_plan_node.mjs). */
  truth?: ClientCombatTruth;
  /** false: plan in one task (the caller's restart budget is spent). Default: yield between passes, `stale` when the state moved. */
  yieldPasses?: boolean; progress?: CombatProgress }): Promise<BrowserStartOutcome> {
  const encounterId = upper(input.encounterId);
  input.progress?.('Reading combat perception…');
  const truth = input.truth ?? await previewTruth(encounterId);
  if (!truth) return { kind: 'gate', reason: 'island-truth' };
  let encounter = encounterFor(encounterId);
  if (!encounter) return { kind: 'gate', reason: 'encounter-arr' };
  let participants: StartParticipant[];
  try { participants = startIsland(encounter, truth, input.seedIds, input.excludedIds); }
  catch (error) { return unportedIslandGate(error); }
  if (!participants.length) return { kind: 'gate', reason: 'no-participants', message: 'No eligible creatures were available to seed a combat.' };
  if (participants.length > 64) return { kind: 'gate', reason: 'start-too-large' };
  // Every participant's FULL compile (the engine reads computed skills / ability buckets), and every resident holding an armed contingency.
  const owners = rosterOf(encounter).filter(r => statusRows(r, 'target').some(s => CONTINGENCY_ARMING_IDS.includes(statusItemId(s)))).map(idOf);
  const hydrateIds = [...new Set([...participants.map(p => p.id), ...owners])];
  if (!await hydrateCompiles(hydrateIds, input.progress)) return { kind: 'gate', reason: 'compile-fetch-failed' };
  encounter = encounterFor(encounterId);
  if (!encounter) return { kind: 'gate', reason: 'encounter-arr' };
  // The roll needs the saved skill's identity, not an adopted skill card. Older hosts without that row still hydrate it normally.
  const initiative = heldInitiativeDefinition(encounterId);
  const defIds = new Set<string>([FORTUNATE_ABILITY_ID]);
  if (!initiative) defIds.add(INITIATIVE_SKILL_ID);
  for (const row of rosterOf(encounter)) {
    if (!hydrateIds.includes(idOf(row))) continue;
    for (const s of [...statusRows(row, 'target'), ...statusRows(row, 'caster')]) { defIds.add(statusItemId(s)); if (!isSelfStatusOrigin(upper(s.status_id), upper(s.status_origin_id)) && UUID.test(upper(s.status_origin_id))) defIds.add(upper(s.status_origin_id)); }
  }
  const referenceIds = new Set(defIds);
  for (const row of rosterOf(encounter)) if (hydrateIds.includes(idOf(row))) for (const id of carrierIds(row)) defIds.add(id);
  input.progress?.('Loading combat abilities…');
  await fetchObjects([...defIds], input.progress);
  const chargeRead = abilityTruthReaders(rosterOf(encounter)[0]!, encounter, new Set(hydrateIds));
  // Inspect every carrier, but only a due opening/turn hook needs its effect closure.
  // Ordinary attacks/spells may apply statuses when cast; they do no work at this boundary.
  for (const id of defIds) {
    const definition = chargeRead.definition(id);
    if (definition && (chargeRead.has(definition, 'triggers_after_combat_begins') || chargeRead.has(definition, 'triggers_when_caster_turn_begins')))
      referenceIds.add(id);
  }
  await hydrateReferences([...referenceIds], input.progress);
  const tried = new Set<string>();
  // OWNER RULING 2026-10-02 (lane A11): a reaction of a player's creature in a combat-begins frame is asked (≤ 5 s) before any real die — the
  // planner answers `consent`, this page asks the controllers, then plans again with the answers fixed (the real dice see them final)
  const consent = createTriggerConsent(encounterId);
  const unavailableDefinitions = new Set<string>();
  const chargeRows = new Map<string, Record<string, unknown>>();
  // Nested skill grants can be bare refs even on a fresh compile. Resolve their saved
  // pools once before the dry/real passes; only a successful empty SELECT means full.
  const chargeHolders = rosterOf(encounter).filter(row => hydrateIds.includes(idOf(row))
    && ABILITY_CHARGE_BUCKETS.some(bucket => chargeRead.entries(row, bucket).some(raw => {
      const id = abilityEntryId(raw), definition = chargeRead.definition(id);
      return definition && chargeRead.has(definition, 'triggers_after_combat_begins') && chargeRead.has(definition, 'ability_charges')
        && entryCharges(asRow(raw) ?? { item_id: id }, id, { value: chargeRead.value, resolveItem: id => chargeRead.definition(upper(id)) })?.currentHeld === false;
    }))).map(idOf);
  if (chargeHolders.length) {
    const saved = await Promise.all(chargeHolders.map(async id => [id, await fetchChargeRows(encounterId, id, 15000)] as const));
    for (const [id, rows] of saved) if (rows) chargeRows.set(id, rows);
  }
  // Five definition fetch/re-plans plus one final plan; unservable optional hooks freeze into GM skips.
  for (let pass = 0, asks = 0; pass <= 5; pass++) {
    input.progress?.(`Planning initiative (${pass + 1})…`);
    encounter = encounterFor(encounterId);
    if (!encounter) return { kind: 'gate', reason: 'encounter-arr' };
    const first = rosterOf(encounter).find(r => idOf(r) === participants[0]!.id) ?? rosterOf(encounter)[0]!;
    const ability = abilityTruthReaders(first, encounter, new Set(hydrateIds));
    const readers: CombatStartReaders = {
      ability: { ...ability, triggerConsent: consent,
        definition: id => upper(id) === INITIATIVE_SKILL_ID && initiative ? initiative : ability.definition(id) },
      turn: work => turnReaders(work),
      exit: input.exitReaders,
      ...clockReaders(input.campaignId),
      uuid: () => crypto.randomUUID().toUpperCase(),
      canFetchOptionalDefinition: id => pass < 5 && !unavailableDefinitions.has(id),
      chargeRows,
    };
    // Yields to the page between the plan's passes (start-roll.ts planCombatStartYielding — fix-bug 2026-10-13: one task froze the tab). A plan
    // whose live inputs moved during a pause is dropped before its real dice, and the WHOLE start is decided again by the caller — the island,
    // its floor gate, the hydrated members and the opening were all derived from the state that moved (Astra review 2026-10-13).
    const planInput = { encounterId, combatId: crypto.randomUUID().toUpperCase(), participants, startedBy: input.seedIds[0] ? upper(input.seedIds[0]) : null,
      rest: input.opening.rest, pre: input.preLastWins, charges: input.charges, rollPrivate: input.rollPrivate };
    let outcome: Awaited<ReturnType<typeof planCombatStartYielding>>;
    if (input.yieldPasses !== false) {
      const watch = watchPlanInputs(encounterId);
      let completed = 0;
      try { outcome = await planCombatStartYielding(planInput, encounter, readers, APPLIERS, browserRollSource(), COMBAT_START_ENGINE,
        beforeReal => { input.progress?.(`Planning initiative (${++completed} passes complete)…`); return watch.unchanged(beforeReal); }); }
      finally { watch.stop(); }
      if (!outcome) { ftDebugLog('combat', { phase: 'startPlanInputsMoved' }); return { kind: 'stale' }; }
    } else outcome = planCombatStart(planInput, encounter, readers, APPLIERS, browserRollSource());
    if (outcome.kind === 'plan') return outcome;
    if (outcome.kind === 'refuse') return { kind: 'gate', reason: outcome.reason, message: outcome.message };
    if (outcome.kind === 'consent') {
      if (consent.final || asks++ >= MAX_ASK_ROUNDS) { consent.final = true; continue; } // past the rounds: every unanswered one declines
      input.progress?.('Waiting for player reactions…');
      await askTriggerConsent(encounterId, participants[0]!.id, consent); pass--; continue;
    }
    const fresh = outcome.ids.filter(id => !tried.has(id));
    if (!fresh.length || pass === 5) return { kind: 'gate', reason: 'start-definitions' };
    fresh.forEach(id => tried.add(id));
    await hydrateReferences(fresh, input.progress);
    for (const id of fresh) if (!readers.ability.definition(id)) unavailableDefinitions.add(id);
  }
  return { kind: 'gate', reason: 'start-definitions' };
}
